'use client';

/* ──────────────────────────────────────────────
 *  Demande de devis intégrée au tunnel (spec Parcours § 7.8,
 *  planche 6) : la prestation est rappelée, le client laisse ses
 *  coordonnées et sa demande. Même circuit que le formulaire de
 *  contact du site (enregistrement, email au pro, captcha) ; ne crée
 *  ni devis ni réservation.
 * ────────────────────────────────────────────── */

import { type JSX, useCallback, useMemo, useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { type StepNav, useRegisterStepNav } from '../../context/StepNavContext';
import { CustomerInfoFields, type CustomerInfoErrors, type CustomerInfoField } from '../shared/CustomerInfoFields';
import { hasCustomerInfoErrors, readCheckoutDraft, validateCustomerInfo } from '../../utils/customer-form';

export function QuoteRequestStep(): JSX.Element {
  const { ctx, callbacks, labels, locale, flagBaseUrl, goBack, professionalName, professionalSlug } = useBooking();
  const prestation = ctx.prestations.find((p): boolean => p.prestation_id === ctx.quotePrestationId);
  const [form, setForm] = useState(() => {
    const draft = ctx.customerInfo ?? readCheckoutDraft(professionalSlug);
    return {
      firstname: draft?.firstname ?? '',
      lastname: draft?.lastname ?? '',
      email: draft?.email ?? '',
      phone: draft?.phone ?? '',
      message: '',
    };
  });
  const [errors, setErrors] = useState<CustomerInfoErrors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useRegisterStepNav(useMemo((): StepNav => ({ onBack: goBack, ownsPrimary: true }), [goBack]));

  const updateField = useCallback((field: CustomerInfoField, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev): CustomerInfoErrors => ({ ...prev, [field]: undefined }));
  }, []);

  const submit = async (): Promise<void> => {
    if (!prestation || !callbacks.onSubmitQuoteRequest || status === 'sending') return;
    const nextErrors = validateCustomerInfo(form, labels);
    setErrors(nextErrors);
    if (hasCustomerInfoErrors(nextErrors)) return;
    setStatus('sending');
    try {
      await callbacks.onSubmitQuoteRequest({
        prestation_id: prestation.prestation_id,
        prestation_name: prestation.name,
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="bt-step">
        <div className="bt-confirmed bt-quote-sent" role="status">
          <div className="bt-confirmed__icon" aria-hidden="true">
            ✓
          </div>
          <h2 className="bt-step-header__title">{labels.quote_sent_title}</h2>
          <p className="bt-confirmed__message">
            {professionalName
              ? labels.quote_sent_text.replace('{name}', professionalName)
              : labels.quote_sent_text_generic}
          </p>
          <button type="button" className="bt-cta bt-cta--secondary bt-cta--mt" onClick={goBack}>
            {labels.quote_back}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{labels.step_quote}</h2>
      </div>

      {prestation ? (
        <p className="bt-express-item">
          <span className="bt-express-item__text">{`${prestation.name} · ${labels.on_quote}`}</span>
        </p>
      ) : null}

      <form
        className="bt-checkout"
        noValidate
        onSubmit={(event): void => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="bt-checkout__section">
          <CustomerInfoFields
            values={form}
            errors={errors}
            onChange={updateField}
            locale={locale}
            flagBaseUrl={flagBaseUrl}
            idPrefix="bt-quote"
            labels={{
              firstname: labels.firstname,
              lastname: labels.lastname,
              email: labels.email,
              phone: labels.phone,
              phone_country_code: labels.phone_country_code,
              phone_placeholder: labels.phone_placeholder,
            }}
          />
          <div className="bt-field bt-field--note">
            <label className="bt-field__label" htmlFor="bt-quote-message">
              {labels.quote_message_label}
            </label>
            <textarea
              id="bt-quote-message"
              className="bt-field__input bt-field__input--textarea"
              value={form.message}
              onChange={(event): void => setForm((prev) => ({ ...prev, message: event.target.value }))}
              rows={4}
            />
          </div>
        </div>

        {status === 'error' ? (
          <p className="bt-error" role="alert">
            {labels.quote_error}
          </p>
        ) : null}

        <button type="submit" className="bt-cta bt-cta--mt" disabled={status === 'sending'}>
          {status === 'sending' ? <span className="bt-spinner bt-spinner--inline" /> : labels.quote_submit}
        </button>
      </form>
    </div>
  );
}
